<template>
  <TableComp
    :headers="headers"
    :name="$t('global.team-members')"
    :status="status"
    :number-of-items="members.length"
    @update:list="updateList"
    emptyImage="team"
  >
    <template #desktop>
      <tr v-for="member in members" :key="member.id">
        <td>
          <div v-if="isVerified(member) && (member.firstName && member.lastName)">
            {{ member.firstName }} {{ member.lastName }}
          </div>
          <div v-else>
            {{ $t("global.na") }}
          </div>
        </td>
        <td class="align-middle">
          <div class="d-flex justify-content-center">
            {{ member.email }}
          </div>
        </td>
        <td class="align-middle">
          <div class="d-flex justify-content-center">
            <Pill
              :variant="getStatusVariant(isVerified(member) ? 'verified' : 'pending-user')"
              :content="isVerified(member) ? 'Verified' : 'Pending'"
            />
          </div>
        </td>
        <td class="align-middle">
          <div class="d-flex flex-wrap justify-content-center">
            <LoadingState v-if="loading()" />
            <div v-else class="d-flex flex-wrap justify-content-center">
              <div v-for="team in teams" :key="team.id">
                <div @click="viewPermissions(getPermissions(team), team.name)" class="perm-tag">
                  <div v-for="member in member.teams" :key="member.id">
                    <div v-if="member.name === team.name" class="mt-1 mx-1">
                      <Tag variant="static" :content="member.name" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </td>
        <td class="text-center align-middle">
          <div class="d-flex justify-content-end mr-3">
            <ActionIcons :actions="actionItems(member)" />
          </div>
        </td>
      </tr>
    </template>
    <template #mobile>
      <TableMobileRow v-for="member in members" :key="member.id" :rows="mobileRows(member)">
        <template #header>
          <div class="d-flex align-items-center">
            <div class="flex-fill">
              <div v-if="isVerified(member) && (member.firstName && member.lastName)">
                {{ member.firstName }} {{ member.lastName }}
              </div>
              <div v-else>
                {{ $t("global.na") }}
              </div>
            </div>
          </div>
        </template>
        <template #content>
          <div class="row">
            <div class="col">{{ $t("global.name") }}:</div>
            <div class="col">
              <div v-if="isVerified(member) && (member.firstName && member.lastName)">
                {{ member.firstName }} {{ member.lastName }}
              </div>
              <div v-else>
                {{ $t("global.na") }}
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col">{{ $t("global.email") }}:</div>
            <div class="col">
              {{ member.email }}
            </div>
          </div>
          <div class="row">
            <div class="col">{{ $t("global.status") }}:</div>
            <div class="col">
              <Pill
                :variant="getStatusVariant(isVerified(member) ? 'verified' : 'pending-user')"
                :content="isVerified(member) ? 'Verified' : 'Pending'"
              />
            </div>
          </div>
          <div class="row">
            <div class="col">{{ $t("global.teams") }}:</div>
            <div class="col">
              <div class="d-flex flex-wrap">
                <LoadingState v-if="status.isLoading" />
                <div v-else v-for="(team, index) in teams" :key="team.id">
                  <div @click="viewPermissions(getPermissions(team), team.name)" class="perm-tag">
                    <div v-for="member in member.teams" :key="member.id">
                      <div
                        v-if="member.name === team.name"
                        class="mt-1"
                        :class="index < teams.length - 1 ? 'mr-4' : 'mr-0'"
                      >
                        <Tag variant="static" :content="member.name" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </TableMobileRow>
    </template>
  </TableComp>
</template>

<script>
import { computed, onMounted } from "@vue/composition-api";
import ActionIcons from "@/components/display/ActionIcons";
import ConfirmationModal from "@/components/display/ConfirmationModal";
import { dateFormat } from "leatherman-js/utils/formatters/time";
import { getStatusVariant } from "@/utils/statusVariant";
import LoadingState from "@/components/display/LoadingState";
import NavLink from "@/classes/NavLink";
import Pill from "@/components/display/Pill";
import store from "@/store";
import TableComp from "@/components/display/table/TableComp";
import TableHeader from "@/classes/TableHeader";
import TableMobileRow from "@/components/display/table/TableMobileRow";
import Tag from "@/components/display/Tag";
import TeamMemberEditModal from "@/components/smart/modals/TeamMemberEdit";
import { timeSince } from "leatherman-js/utils/formatters/time";
import ViewTeamPermissions from "@/components/smart/modals/ViewTeamPermissions";

export default {
  name: "TeamMemberTable",
  props: {
    members: Array,
    status: Object,
  },
  components: {
    ActionIcons,
    Pill,
    Tag,
    TableComp,
    TableMobileRow,
    LoadingState,
  },
  setup(props, { root }) {
    const headers = [
      new TableHeader(root.$t("global.name"), null, null),
      new TableHeader(root.$t("global.email"), "justify-content-center", null),
      new TableHeader(root.$t("global.status"), "justify-content-center", null),
      new TableHeader(root.$t("global.teams"), "justify-content-center", null),
      new TableHeader(),
    ];
    const teams = computed(() => store.getters["team/getList"]);
    const teamsStatus = computed(() => store.getters["team/getListStatus"]);
    const resourceRoles = computed(() => store.getters["team/resourceRoles"]);
    const rolesStatus = computed(() => store.getters["team/rolesStatus"]);

    const isVerified = (member) => {
      return member.status !== "pending" && member.verified === 1;
    };

    const getPermissions = (team) => {
      if (!resourceRoles.value) {
        return [];
      }

      return resourceRoles.value.map((role) => ({
        name: role.name,
        actions: team.permissions[role.name],
      }));
    };

    const updateList = () => {
      store.dispatch("team/refreshMembers");
    };

    const editMember = (member) => {
      store.dispatch("modals/open", {
        template: TeamMemberEditModal,
        data: { member },
      });
    };

    const confirmDeleteMember = (member) => {
      return store.dispatch("team/deleteMember", member.id).then(() => {
        store.dispatch("team/refreshMembers");
      });
    };

    const deleteMember = (member) => {
      store.dispatch("modals/open", {
        template: ConfirmationModal,
        data: {
          confirm: confirmDeleteMember.bind(null, member),
          errorMessage: root.$t("global.error-deleting-team-member", {
            name:
              isVerified(member) && member.firstName && member.lastName
                ? member.firstName + " " + member.lastName
                : member.email,
          }),
          message: root.$t("global.confirm-team-member-delete", {
            name:
              isVerified(member) && member.firstName && member.lastName
                ? member.firstName + " " + member.lastName
                : member.email,
          }),
          title: root.$t("global.delete-team-member"),
          variant: "danger",
          onSuccess: () =>
            store.dispatch("toasts/addToast", {
              variant: "info",
              message: root.$t("toasts.team-member-removed-message"),
            }),
        },
      });
    };

    const actionItems = (member) => [
      new NavLink(
        root.$t("global.edit-team-member"),
        "edit",
        () => editMember(member),
        false,
        null,
        "epsilon"
      ),
      new NavLink(
        root.$t("global.team-member"),
        "trash",
        () => deleteMember(member),
        false,
        null,
        "epsilon"
      ),
    ];

    const mobileRows = (member) => [
      {
        title: root.$t("global.name"),
        data:
          isVerified(member) && member.firstName && member.lastName
            ? member.firstName + " " + member.lastName
            : member.email,
      },
      { title: root.$t("global.email"), data: member.email },
    ];

    const viewPermissions = (permissions, team) => {
      store.dispatch("modals/open", {
        template: ViewTeamPermissions,
        data: { permissions, team },
      });
    };

    onMounted(() => {
      store.dispatch("team/getList").then(() => store.dispatch("team/getRoles"));
    });

    const loading = () => rolesStatus.value.isLoading || teamsStatus.value.isLoading;

    return {
      dateFormat,
      getStatusVariant,
      headers,
      getPermissions,
      resourceRoles,
      actionItems,
      loading,
      mobileRows,
      rolesStatus,
      teams,
      teamsStatus,
      timeSince,
      updateList,
      viewPermissions,
      isVerified,
    };
  },
};
</script>
<style lang="scss" scoped>
.perm-tag:hover {
  cursor: pointer;
}
</style>
